import { ThrowLiteError } from '../lite-error.js';
import { U8, U8C } from '../engine/typed-arrays.js';
import { TU } from '../engine/gpu-flags.js';
import { getOrCreateSampler, acquireTexture } from '../resource/gpu-pool.js';
import { getCompressedFormat } from './compressed-formats.js';

let _ktx2DecoderUrl = "https://cdn.babylonjs.com/babylon.ktx2Decoder.js";
let _ktx2WasmUrls = null;
function setKtx2DecoderUrl(url, wasmUrls) {
  _ktx2DecoderUrl = url;
  _ktx2WasmUrls = wasmUrls ?? null;
}
let _ktx2DecoderPromise = null;
const GL_RGBA8 = 32856;
const GL_R8 = 33321;
const GL_RG8 = 33323;
const RGBA_CAPS = { astc: false, bptc: false, s3tc: false, pvrtc: false, etc2: false, etc1: false };
function deviceKtx2Caps(engine) {
  const f = engine._device.features;
  const bc = f.has("texture-compression-bc");
  const etc2 = f.has("texture-compression-etc2");
  return {
    astc: f.has("texture-compression-astc"),
    bptc: bc,
    // BC6H/BC7
    s3tc: bc,
    // BC1/BC2/BC3
    pvrtc: false,
    // unsupported on WebGPU
    etc2,
    etc1: etc2
    // ETC1 content transcodes on ETC2-capable GPUs
  };
}
function loadKtx2Decoder() {
  if (_ktx2DecoderPromise) {
    return _ktx2DecoderPromise;
  }
  _ktx2DecoderPromise = new Promise((resolve, reject) => {
    const w = globalThis;
    const init = () => {
      const mod = w.KTX2DECODER;
      if (!mod) {
        reject(new Error("KTX2: decoder global KTX2DECODER not found after script load"));
        return;
      }
      mod.MSCTranscoder.UseFromWorkerThread = false;
      mod.WASMMemoryManager.LoadBinariesFromCurrentThread = true;
      if (_ktx2WasmUrls) {
        const m = mod;
        for (const tName of Object.keys(_ktx2WasmUrls)) {
          const t = m[tName];
          if (t) {
            for (const prop of Object.keys(_ktx2WasmUrls[tName])) {
              t[prop] = _ktx2WasmUrls[tName][prop];
            }
          }
        }
      }
      resolve(new mod.KTX2Decoder());
    };
    if (w.KTX2DECODER) {
      init();
      return;
    }
    const script = document.createElement("script");
    script.src = _ktx2DecoderUrl;
    script.async = true;
    script.onload = init;
    script.onerror = () => reject(new Error(`KTX2: failed to load ${script.src}`));
    document.head.appendChild(script);
  });
  _ktx2DecoderPromise.catch(() => {
    _ktx2DecoderPromise = null;
  });
  return _ktx2DecoderPromise;
}
function srgbFormat(format) {
  switch (format) {
    case "rgba8unorm":
      return "rgba8unorm-srgb";
    case "bc1-rgba-unorm":
      return "bc1-rgba-unorm-srgb";
    case "bc2-rgba-unorm":
      return "bc2-rgba-unorm-srgb";
    case "bc3-rgba-unorm":
      return "bc3-rgba-unorm-srgb";
    case "bc7-rgba-unorm":
      return "bc7-rgba-unorm-srgb";
    case "etc2-rgb8unorm":
      return "etc2-rgb8unorm-srgb";
    case "etc2-rgb8a1unorm":
      return "etc2-rgb8a1unorm-srgb";
    case "etc2-rgba8unorm":
      return "etc2-rgba8unorm-srgb";
    case "astc-4x4-unorm":
      return "astc-4x4-unorm-srgb";
    case "astc-5x4-unorm":
      return "astc-5x4-unorm-srgb";
    case "astc-5x5-unorm":
      return "astc-5x5-unorm-srgb";
    case "astc-6x5-unorm":
      return "astc-6x5-unorm-srgb";
    case "astc-6x6-unorm":
      return "astc-6x6-unorm-srgb";
    case "astc-8x5-unorm":
      return "astc-8x5-unorm-srgb";
    case "astc-8x6-unorm":
      return "astc-8x6-unorm-srgb";
    case "astc-8x8-unorm":
      return "astc-8x8-unorm-srgb";
    case "astc-10x5-unorm":
      return "astc-10x5-unorm-srgb";
    case "astc-10x6-unorm":
      return "astc-10x6-unorm-srgb";
    case "astc-10x8-unorm":
      return "astc-10x8-unorm-srgb";
    case "astc-10x10-unorm":
      return "astc-10x10-unorm-srgb";
    case "astc-12x10-unorm":
      return "astc-12x10-unorm-srgb";
    case "astc-12x12-unorm":
      return "astc-12x12-unorm-srgb";
    default:
      return format;
  }
}
function uncompressedInfo(glFormat) {
  switch (glFormat) {
    case GL_RGBA8:
      return { format: "rgba8unorm", bytesPerPixel: 4 };
    case GL_R8:
      return { format: "r8unorm", bytesPerPixel: 1 };
    case GL_RG8:
      return { format: "rg8unorm", bytesPerPixel: 2 };
    default:
      return null;
  }
}
function validateDecoded(decoded) {
  if (decoded.errors) {
    ThrowLiteError(552, decoded.errors);
  }
  if (!decoded.mipmaps.length) {
    ThrowLiteError(553);
  }
  for (let i = 0; i < decoded.mipmaps.length; i++) {
    if (!decoded.mipmaps[i]?.data) {
      ThrowLiteError(554, i);
    }
  }
  return decoded.mipmaps;
}
function makeSampler(engine, mipCount) {
  return getOrCreateSampler(engine, {
    addressModeU: "repeat",
    addressModeV: "repeat",
    minFilter: "linear",
    magFilter: "linear",
    mipmapFilter: mipCount > 1 ? "linear" : "nearest",
    maxAnisotropy: mipCount > 1 ? 4 : 1
  });
}
function uploadCompressed(engine, mips, format, sRGB) {
  if (!engine._device.features.has(format.feature)) {
    ThrowLiteError(555, format.feature);
  }
  const width = mips[0].width;
  const height = mips[0].height;
  const texture = engine._device.createTexture({
    size: { width, height },
    format: sRGB ? srgbFormat(format.gpuFormat) : format.gpuFormat,
    mipLevelCount: mips.length,
    usage: TU.TEXTURE_BINDING | TU.COPY_DST
  });
  for (let level = 0; level < mips.length; level++) {
    const mip = mips[level];
    const blocksPerRow = Math.ceil(mip.width / format.blockW);
    const rowBytes = blocksPerRow * format.blockBytes;
    const copyW = blocksPerRow * format.blockW;
    const copyH = Math.ceil(mip.height / format.blockH) * format.blockH;
    engine._device.queue.writeTexture({ texture, mipLevel: level }, mip.data, { bytesPerRow: rowBytes }, { width: copyW, height: copyH });
  }
  const tex2d = { texture, view: texture.createView(), sampler: makeSampler(engine, mips.length), width, height, invertY: true };
  acquireTexture(tex2d);
  return tex2d;
}
function uploadUncompressed(engine, mips, info, sRGB) {
  const width = mips[0].width;
  const height = mips[0].height;
  const texture = engine._device.createTexture({
    size: { width, height },
    format: sRGB ? srgbFormat(info.format) : info.format,
    mipLevelCount: mips.length,
    usage: TU.TEXTURE_BINDING | TU.COPY_DST
  });
  for (let level = 0; level < mips.length; level++) {
    const mip = mips[level];
    const expected = mip.width * mip.height * info.bytesPerPixel;
    if (mip.data.length !== expected) {
      ThrowLiteError(556, level, mip.data.length, expected);
    }
    engine._device.queue.writeTexture(
      { texture, mipLevel: level },
      mip.data,
      { bytesPerRow: mip.width * info.bytesPerPixel },
      { width: mip.width, height: mip.height }
    );
  }
  const tex2d = { texture, view: texture.createView(), sampler: makeSampler(engine, mips.length), width, height, invertY: true };
  acquireTexture(tex2d);
  return tex2d;
}
async function decodeKtx2Async(engine, buffer) {
  const decoder = await loadKtx2Decoder();
  const decoded = await decoder.decode(new U8(buffer), deviceKtx2Caps(engine));
  validateDecoded(decoded);
  return decoded;
}
async function uploadKtx2Texture2D(engine, buffer, sRGB) {
  const decoded = await decodeKtx2Async(engine, buffer);
  const mips = decoded.mipmaps;
  const compressed = getCompressedFormat(decoded.transcodedFormat);
  if (compressed) {
    return uploadCompressed(engine, mips, compressed, sRGB);
  }
  const uncompressed = uncompressedInfo(decoded.transcodedFormat);
  if (uncompressed) {
    return uploadUncompressed(engine, mips, uncompressed, sRGB);
  }
  ThrowLiteError(557, decoded.transcodedFormat.toString(16));
}
async function loadKtx2Texture2D(engine, url, sRGB = false) {
  const resp = await fetch(url);
  if (!resp.ok) {
    ThrowLiteError(558, resp.status, url);
  }
  return uploadKtx2Texture2D(engine, await resp.arrayBuffer(), sRGB);
}
async function decodeKtx2ImageBitmapFromBuffer(buffer) {
  const decoder = await loadKtx2Decoder();
  const decoded = await decoder.decode(new U8(buffer), RGBA_CAPS, { forceRGBA: true });
  const mip0 = validateDecoded(decoded)[0];
  if (mip0.data.length !== mip0.width * mip0.height * 4) {
    ThrowLiteError(559);
  }
  const pixels = new U8C(mip0.data.length);
  pixels.set(mip0.data);
  return createImageBitmap(new ImageData(pixels, mip0.width, mip0.height));
}

export { RGBA_CAPS, decodeKtx2Async, decodeKtx2ImageBitmapFromBuffer, loadKtx2Decoder, loadKtx2Texture2D, makeSampler, setKtx2DecoderUrl, srgbFormat, uncompressedInfo, uploadKtx2Texture2D };
//# sourceMappingURL=ktx2-loader.js.map
